import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Headphones, Loader2, Music, Pause, Play, Tag } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import ShareButton from '../components/ShareButton';
import TrackActions from '../components/TrackActions';
import MixtapeDownloadGate from '../components/MixtapeDownloadGate';
import { useAudio } from '../contexts/AudioContext';
import { AdBanner } from '../components/GoogleAd';

const API = process.env.REACT_APP_BACKEND_URL;

const MixtapeDetailPage = () => {
  const { mixtapeId } = useParams();
  const [mixtape, setMixtape] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolving, setResolving] = useState(false);
  const { currentTrack, isPlaying, playMixtape, togglePlay } = useAudio();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/api/mixtapes/${encodeURIComponent(mixtapeId)}`)
      .then((response) => {
        if (cancelled) return;
        setMixtape(response.data?.mixtape || null);
        setRelated(response.data?.related || []);
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError.response?.status === 404 ? 'Mixtape not found' : 'Failed to load mixtape');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mixtapeId]);

  const isCurrent = Boolean(mixtape && currentTrack?.title === mixtape.title && currentTrack?.type === 'mixtape');

  const handlePlay = useCallback(async () => {
    if (!mixtape) return;
    if (isCurrent) {
      togglePlay();
      return;
    }
    setResolving(true);
    try {
      const response = await axios.get(`${API}/api/playblock/resolve/${encodeURIComponent(mixtape.id)}`);
      if (response.data?.audio_url) playMixtape({ ...mixtape, resolved_audio_url: response.data.audio_url, playblock: true });
      else playMixtape(mixtape, true);
    } catch {
      playMixtape(mixtape, true);
    } finally {
      setResolving(false);
    }
  }, [isCurrent, mixtape, playMixtape, togglePlay]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 text-primary animate-spin" /></div>;

  if (error || !mixtape) {
    return <div className="min-h-screen py-24 px-4"><div className="max-w-2xl mx-auto text-center"><h1 className="text-3xl font-bold mb-4">{error || 'Mixtape not found'}</h1><Link to="/mixtapes" className="text-primary hover:underline">Back to Mixtapes</Link></div></div>;
  }

  const creatorLine = Array.isArray(mixtape.creator_names) && mixtape.creator_names.length ? mixtape.creator_names.join(' × ') : mixtape.dj;
  const creatorProfiles = Array.isArray(mixtape.creator_profiles) ? mixtape.creator_profiles : [];
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MusicRecording',
    name: mixtape.title,
    byArtist: { '@type': 'MusicGroup', name: creatorLine || 'Rastyle Creator' },
    genre: mixtape.genre,
    image: mixtape.cover,
    description: mixtape.description || `${mixtape.title} by ${creatorLine || 'Rastyle Creator'}`,
    datePublished: mixtape.hearthis_created_at || mixtape.created_at,
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <SEO title={`${mixtape.title} — ${creatorLine || 'Rastyle'}`} description={mixtape.description || `Stream ${mixtape.title} on Rastyle Republik.`} image={mixtape.cover} url={`/mixtapes/${mixtape.id}`} type="music.song" jsonLd={jsonLd} />
      <div className="max-w-5xl mx-auto">
        <Link to="/mixtapes" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8"><ArrowLeft className="w-4 h-4" /> Back to Mixtapes</Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="relative aspect-square rounded-sm overflow-hidden group bg-zinc-900">
            <img src={mixtape.cover || '/logo.png'} alt={mixtape.title} className="w-full h-full object-cover" data-testid="mixtape-cover" />
            <div className={`absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity ${isCurrent ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
              <button onClick={handlePlay} disabled={resolving} className="w-20 h-20 rounded-full bg-primary flex items-center justify-center hover:scale-110 transition-transform" aria-label={isCurrent && isPlaying ? 'Pause mixtape' : 'Play mixtape'}>
                {resolving ? <Loader2 className="w-10 h-10 text-black animate-spin" /> : isCurrent && isPlaying ? <Pause className="w-10 h-10 text-black" /> : <Play className="w-10 h-10 text-black ml-1" />}
              </button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col justify-center">
            <div className="flex flex-wrap gap-2 mb-3 text-xs uppercase tracking-widest font-bold">
              <span className="text-primary">{mixtape.genre || 'Reggae'}</span>
              <span className="text-muted-foreground">· {mixtape.content_type || 'mixtape'}</span>
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl mb-3">{mixtape.title}</h1>
            {creatorProfiles.length > 0 ? (
              <div className="mb-6 flex flex-wrap items-center gap-2 text-sm" data-testid="mixtape-creator-credits">
                {creatorProfiles.map((profile) => (
                  <Link
                    key={profile.creator_id}
                    to={`/creator/${profile.slug || profile.creator_id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 hover:border-primary hover:text-primary"
                  >
                    {profile.profile_image && <img src={profile.profile_image} alt="" className="h-6 w-6 rounded-full object-cover" />}
                    <span>{profile.display_name}</span>
                    {profile.role === 'featured' && <span className="text-[9px] font-bold uppercase tracking-wider text-primary">Featured</span>}
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-lg text-muted-foreground mb-6">by {creatorLine || 'Rastyle Creator'}</p>
            )}

            <div className="grid grid-cols-2 gap-4 mb-6 text-sm text-muted-foreground">
              {mixtape.duration_formatted && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" />{mixtape.duration_formatted}</div>}
              {mixtape.plays && <div className="flex items-center gap-2"><Headphones className="w-4 h-4 text-primary" />{mixtape.plays} plays</div>}
              {mixtape.hearthis_created_at && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-primary" />{new Date(mixtape.hearthis_created_at).toLocaleDateString()}</div>}
              {mixtape.bpm && <div className="flex items-center gap-2"><Music className="w-4 h-4 text-primary" />{mixtape.bpm} BPM</div>}
            </div>

            {Array.isArray(mixtape.tags) && mixtape.tags.length > 0 && <div className="flex flex-wrap gap-2 mb-6">{mixtape.tags.slice(0, 8).map((tag) => <span key={tag} className="px-3 py-1 text-xs bg-white/5 border border-white/10 rounded-sm"><Tag className="w-3 h-3 inline mr-1" />{tag}</span>)}</div>}

            <div className="flex flex-wrap items-center gap-3">
              <button onClick={handlePlay} disabled={resolving} className="flex items-center gap-2 px-6 py-3 bg-primary text-black font-bold rounded-sm disabled:opacity-50">{resolving ? <Loader2 className="w-5 h-5 animate-spin" /> : isCurrent && isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}{isCurrent && isPlaying ? 'Pause' : 'Play Now'}</button>
              <ShareButton title={mixtape.title} url={`/mixtapes/${mixtape.id}`} type="mixtape" contentId={mixtape.id} />
              <TrackActions track={mixtape} />
            </div>

            {/* The backend deliberately hides the raw URL. The gate requests it after consent. */}
            {mixtape.downloadable && (
              <div className="mt-4" data-testid="mixtape-detail-download">
                <MixtapeDownloadGate mixtapeId={mixtape.id} mixtapeTitle={mixtape.title} dj={creatorLine || mixtape.dj} variant="full" testIdSuffix="detail" />
              </div>
            )}
          </motion.div>
        </div>

        {mixtape.description && <div className="glass-card rounded-sm p-6 mb-12"><h2 className="font-heading font-bold text-lg mb-3">About this Mix</h2><div className="text-muted-foreground whitespace-pre-line leading-relaxed" dangerouslySetInnerHTML={{ __html: mixtape.description }} /></div>}
        <AdBanner placement="mixtape_detail" />

        {related.length > 0 && <section className="mt-12"><h2 className="font-heading font-bold text-2xl mb-6">Related Mixes</h2><div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">{related.map((mix) => <Link key={mix.id} to={`/mixtapes/${mix.id}`} className="glass-card rounded-sm overflow-hidden group"><div className="aspect-square overflow-hidden"><img src={mix.cover || '/logo.png'} alt={mix.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /></div><div className="p-3"><h3 className="text-xs font-semibold truncate">{mix.title}</h3><p className="text-xs text-muted-foreground truncate">{mix.dj}</p></div></Link>)}</div></section>}
      </div>
    </div>
  );
};

export default MixtapeDetailPage;
